import { useEffect, useState, useLayoutEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import ContentRenderer from '../components/content/ContentRenderer/ContentRenderer'
import LessonPlayer from '../components/course/LessonPlayer'
import { useUser } from '../contexts/user'
import { authApi } from '../lib/auth'
import type { CourseDetail } from '../lib/content'
import {
  FaArrowRight,
  FaChevronDown,
  FaChevronRight,
  FaCheckCircle,
  FaLock,
  FaGithub,
  FaExternalLinkAlt,
  FaBookOpen,
  FaShieldAlt,
  FaStar,
  FaQuoteLeft,
  FaPlayCircle,
} from 'react-icons/fa'
import { COURSE_EXERCISES, exerciseUrl, exerciseFor } from '../config/courseExercises'
import { lessonKindMeta, lessonDisplayTitle } from '../config/lessonKind'
import { ContentColumn } from '../components/layout/ContentColumn/ContentColumn'
import { EVENT, trackCheckout } from '../utils/track'
import { authUrlForCheckout } from '../lib/checkoutIntent'
import CourseProgress, { type Progress } from '../components/course/CourseProgress'

function CourseSlugPage({ course }: { course: CourseDetail | null }) {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { user, entitlements } = useUser()

  const modules = course?.modules ?? []
  const totalLessons = modules.reduce((n, m) => n + m.lessons.length, 0)

  // How much of the course is actually recorded, in the buyer's words.
  //
  // Absent from an older backend, and absent is not zero: a missing field must
  // read as "we are not saying" rather than as "none of it is recorded", which
  // would be a worse lie than the one this is fixing. So every use below is
  // guarded on `coverage` being present.
  // Loaded client-side rather than in the loader: it is per-user, and putting it
  // in the SSR payload would make the course page uncacheable for everybody to
  // show a bar to the few people signed in.
  const [progress, setProgress] = useState<Progress | null>(null)
  useEffect(() => {
    if (!user || !slug) { setProgress(null); return }
    let cancelled = false
    void authApi.courseProgress(slug).then((p) => { if (!cancelled) setProgress(p) })
    return () => { cancelled = true }
  }, [user, slug])

  const coverage = course?.videoCoverage
  const coverageLabel = !coverage
    ? null
    : coverage.recorded === 0
      ? 'no recordings yet, written lessons throughout'
      : coverage.recorded >= coverage.total
        ? 'every lesson recorded'
        : `${coverage.recorded} of ${coverage.total} lessons recorded so far`
  // Live counts of what the course includes, by content kind. Computed from the
  // real data so the "what's included" numbers can never drift from the content.
  const byKind = modules
    .flatMap((m) => m.lessons)
    .reduce((acc, l) => {
      const k = l.kind && l.kind !== 'lesson' ? l.kind : 'lesson'
      acc[k] = (acc[k] ?? 0) + 1
      return acc
    }, {} as Record<string, number>)
  const pluralize = (n: number, s: string) =>
    n === 1 ? s : s.endsWith('y') ? `${s.slice(0, -1)}ies` : `${s}s`
  const included = [
    { kind: 'lesson', count: byKind.lesson ?? 0, noun: 'lesson' },
    { kind: 'cheatsheet', count: byKind.cheatsheet ?? 0, noun: 'cheat sheet' },
    { kind: 'worked-design', count: byKind['worked-design'] ?? 0, noun: 'worked design' },
    { kind: 'case-study', count: byKind['case-study'] ?? 0, noun: 'case study' },
    { kind: 'lab', count: byKind.lab ?? 0, noun: 'lab' },
    { kind: 'project', count: byKind.project ?? 0, noun: 'project' },
    { kind: 'field-guide', count: byKind['field-guide'] ?? 0, noun: 'tool field guide' },
  ].filter((x) => x.count > 0)
  const exerciseSet = slug ? COURSE_EXERCISES[slug] : undefined
  const testimonials = course?.testimonials ?? []
  const entitled = !!course && entitlements.includes(course.productSlug)
  const lockedForUser = !!course && course.paid && !entitled

  const [openModules, setOpenModules] = useState<Set<string>>(() =>
    modules.length > 0 ? new Set([modules[0].slug]) : new Set(),
  )
  const [checkoutError, setCheckoutError] = useState('')
  const [checkoutBusy, setCheckoutBusy] = useState(false)

  useEffect(() => {
    if (modules.length > 0) setOpenModules(new Set([modules[0].slug]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course?.slug])

  useLayoutEffect(() => {
    const measure = () => {
      const headerEl = document.querySelector('header') as HTMLElement | null
      const stickyNavEl = document.querySelector('.sticky.top-0') as HTMLElement | null
      const headerH = headerEl?.offsetHeight ?? 0
      const navH = stickyNavEl?.offsetHeight ?? 0
      document.documentElement.style.setProperty('--header-nav-height', `${headerH + navH}px`)
    }
    measure()
    requestAnimationFrame(() => measure())
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  const toggleModule = (moduleSlug: string) => {
    setOpenModules((prev) => {
      const next = new Set(prev)
      if (next.has(moduleSlug)) next.delete(moduleSlug)
      else next.add(moduleSlug)
      return next
    })
  }

  // `location` tells the three Unlock buttons apart (hero, sticky buy card,
  // bottom CTA). Behaviour is identical; only the reporting differs.
  async function handleCheckout(location: string) {
    if (!course) return
    const at = { location, courseSlug: course.slug, price: course.price }
    if (!user) {
      // Signed out, so this press never reaches Stripe. Counting it is the only
      // way to tell "nobody wants it" apart from "the login wall ate the sale".
      trackCheckout(EVENT.checkoutBlocked, { ...at, reason: 'signed_out' })
      // The intent travels with them. Without the checkout param they land back
      // on this page after signing up and have to find the button again, with
      // the decision already made; with it, authentication goes straight to
      // Stripe. See lib/checkoutIntent.ts for what that cost on 31 Aug.
      navigate(
        authUrlForCheckout('/signup', {
          redirect: `/courses/${slug}`,
          productSlug: course.productSlug,
        }),
      )
      return
    }
    setCheckoutError('')
    setCheckoutBusy(true)
    try {
      const { url } = await authApi.checkout(course.productSlug)
      trackCheckout(EVENT.checkoutStart, at)
      window.location.href = url
    } catch (e) {
      const message = (e as Error).message || 'Could not start checkout. Please try again.'
      trackCheckout(EVENT.checkoutFailed, { ...at, reason: message })
      setCheckoutError(message)
      setCheckoutBusy(false)
    }
  }

  if (!course) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-brand-cream">
        <h1 className="font-heading text-display font-bold text-brand-navy mb-4">404</h1>
        <h2 className="font-heading text-h3 font-bold text-brand-navy mb-8">Course Not Found</h2>
        <Link to="/courses" className="font-heading text-cta font-bold text-brand-navy underline">Back to Courses</Link>
      </div>
    )
  }

  const description = course.shortDescription || course.title || ''
  // The pill says "Updated", so it tracks the last content change, not the publish
  // date. publishedAt is a hand-set field and can sit in the future, which read as
  // "Updated September 2026" on a page served in July.
  const updatedSource = course.updatedAt || course.publishedAt
  const publishedLabel = updatedSource
    ? new Date(updatedSource).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', year: 'numeric' })
    : null
  const priceLabel = course.price === 0 ? 'Free' : `$${course.price}`
  const intro = course.intro || course.introBody || ''
  // First lesson the current user can actually open (free course → first lesson;
  // paid + locked → first free-preview lesson).
  const firstOpenLesson = lockedForUser
    ? modules.flatMap((m) => m.lessons).find((l) => l.isFree)?.slug
    : modules[0]?.lessons[0]?.slug

  // Free-preview count, for the secondary CTA. A locked course usually has several
  // free lessons, but the only way to find them used to be scrolling the curriculum
  // and spotting a "Free" tag, so the paid button was the only route out of the hero.
  const freeLessonCount = lockedForUser
    ? modules.flatMap((m) => m.lessons).filter((l) => l.isFree).length
    : 0

  // Takes its placement so the same button in the hero and in the bottom CTA are
  // told apart in the funnel. Identical markup either way.
  const primaryCta = (location: string) => lockedForUser ? (
    <button
      onClick={() => handleCheckout(location)}
      disabled={checkoutBusy}
      className="group inline-flex items-center justify-center gap-2 whitespace-nowrap bg-white text-brand-navy font-heading text-cta font-bold px-8 py-4 rounded-smooth hover:opacity-90 transition-opacity disabled:opacity-60"
    >
      {checkoutBusy ? 'Starting checkout…' : `Unlock full course (${priceLabel})`}
      <FaArrowRight className="text-sm transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" />
    </button>
  ) : firstOpenLesson ? (
    <Link
      to={`/courses/${slug}/${firstOpenLesson}`}
      className="group inline-flex items-center justify-center gap-2 whitespace-nowrap bg-white text-brand-navy font-heading text-cta font-bold px-8 py-4 rounded-smooth hover:opacity-90 transition-opacity"
    >
      Start Learning
      <FaArrowRight className="text-sm transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" />
    </Link>
  ) : null

  const freeCta =
    lockedForUser && firstOpenLesson && freeLessonCount > 0 ? (
      <Link
        to={`/courses/${slug}/${firstOpenLesson}`}
        className="group inline-flex items-center justify-center gap-2 whitespace-nowrap border border-white/30 text-white font-heading text-cta font-bold px-8 py-4 rounded-smooth hover:border-white/60 transition-colors duration-150"
      >
        Read {freeLessonCount} lessons free
        <FaArrowRight className="text-sm transition-transform duration-150 group-hover:translate-x-0.5" aria-hidden="true" />
      </Link>
    ) : null

  // SEO (title, OG/Twitter, Course + Breadcrumb JSON-LD) is emitted by the route
  // meta() in routes/course-slug.tsx so it renders in the SSR document head.
  return (
    <>
      <div className="min-h-screen bg-brand-cream">
        {/* ── Hero ── */}
        <section className="bg-brand-navy py-20 md:py-28">
          {/* data-draw-anchor: stable handle for the annotation layer (DrawingLayer) */}
          <ContentColumn anchor="course-hero" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={course.video ? 'grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-12 lg:items-center' : ''}>
              <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <span className="font-heading text-overline uppercase text-white/60">{priceLabel}</span>
              {course.level && (
                <>
                  <span className="text-white/30 font-heading text-caption" aria-hidden="true">/</span>
                  <span className="font-heading text-overline uppercase text-white/60">{course.level}</span>
                </>
              )}
              {lockedForUser && (
                <>
                  <span className="text-white/30 font-heading text-caption" aria-hidden="true">/</span>
                  <span className="inline-flex items-center gap-1.5 font-heading text-overline uppercase text-white/60">
                    <FaLock className="text-[10px]" aria-hidden="true" /> First module free
                  </span>
                </>
              )}
              {entitled && (
                <>
                  <span className="text-white/30 font-heading text-caption" aria-hidden="true">/</span>
                  <span className="inline-flex items-center gap-1.5 font-heading text-overline uppercase text-brand-sky">
                    <FaCheckCircle className="text-[10px]" aria-hidden="true" /> Owned
                  </span>
                </>
              )}
            </div>

            <h1 className="font-heading text-display font-bold text-white mb-6">{course.title}</h1>

            {description && (
              <p className="font-body text-body-lg text-white/80 mb-10 max-w-3xl">{description}</p>
            )}

            {/* The button leads, the stats sit under it. Side by side pushed the
                pills into a narrow column that wrapped onto four ragged lines. */}
            <div className="flex flex-col items-start gap-5 mb-0">
              <div className="flex flex-wrap items-center gap-3">
                {primaryCta('course_hero')}
                {freeCta}
              </div>

              <div className="flex flex-wrap items-center gap-2 font-heading text-caption">
                {[
                  { key: 'modules', count: modules.length, label: 'modules' },
                  ...included.map((item) => ({
                    key: item.kind,
                    count: item.count,
                    label: pluralize(item.count, item.noun),
                  })),
                ].map((s) => (
                  <span
                    key={s.key}
                    className="inline-flex items-baseline gap-1.5 rounded-full bg-white/10 px-3 py-1.5"
                  >
                    <span className="font-bold text-white">{s.count}</span>
                    <span className="text-white/65">{s.label}</span>
                  </span>
                ))}
                {publishedLabel && (
                  <span className="inline-flex items-center rounded-full bg-white/5 px-3 py-1.5 text-white/55">
                    Updated {publishedLabel}
                  </span>
                )}
                {testimonials.length > 0 && (
                  <a
                    href="#course-reviews"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 transition-colors hover:bg-white/15"
                  >
                    <FaStar className="text-brand-sky text-[11px]" aria-hidden="true" />
                    <span className="font-bold text-white">5.0</span>
                    <span className="text-white/65">
                      {testimonials.length} alumni review{testimonials.length !== 1 ? 's' : ''}
                    </span>
                  </a>
                )}
              </div>
            </div>

            {lockedForUser && course.price > 0 && (
              <div className="mt-6 flex flex-col gap-2">
                <p className="font-body text-body-sm">
                  <span className="text-white/40 line-through">$1,500 live cohort</span>
                  <span className="mx-2 text-white/30" aria-hidden="true">·</span>
                  <span className="text-white/85">${course.price} self-paced, same curriculum</span>
                </p>
                <p className="inline-flex items-center gap-2 font-body text-body-sm text-white/70">
                  <FaShieldAlt className="text-brand-sky text-xs shrink-0" aria-hidden="true" />
                  Full refund if the first module doesn&apos;t change how you design.
                </p>
              </div>
            )}
            {checkoutError && <p className="font-body text-body-sm text-red-300 mt-4">{checkoutError}</p>}
              </div>

              {course.video && (
                <div className="lg:pt-1">
                  <LessonPlayer
                    video={course.video}
                    poster={course.image?.url ?? null}
                    track={{
                      id: `trailer:${course.slug}`,
                      title: `${course.title} (trailer)`,
                      surface: 'course_trailer',
                      props: {
                        courseSlug: course.slug,
                        courseTitle: course.title,
                        paid: course.paid ? 'true' : 'false',
                      },
                    }}
                  />
                </div>
              )}
            </div>
          </ContentColumn>
        </section>

        {/* ── Breadcrumb ── */}
        <div className="border-b border-border-subtle">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <nav aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 font-heading text-overline uppercase text-ink-muted">
                <li><Link to="/" className="hover:text-brand-navy transition-colors duration-150">Home</Link></li>
                <li aria-hidden="true">/</li>
                <li><Link to="/courses" className="hover:text-brand-navy transition-colors duration-150">Courses</Link></li>
                <li aria-hidden="true">/</li>
                <li className="text-brand-navy" aria-current="page">{course.title}</li>
              </ol>
            </nav>
          </div>
        </div>

        {/* ── About + Curriculum (left) | What You'll Learn (right) ── */}
        <section className="py-16 md:py-20">
          <ContentColumn anchor="course-body" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
              {/* Left: About + Curriculum */}
              <div className="lg:col-span-3 space-y-16">
                <div>
                  <h2 className="font-heading text-h2 font-bold text-brand-navy mb-6">About This Course</h2>
                  {intro ? (
                    <div className="font-body text-body text-ink-soft">
                      <ContentRenderer content_md={intro} maxSize="none" />
                    </div>
                  ) : (
                    <p className="font-body text-body text-ink-soft">{description}</p>
                  )}
                </div>

                <div>
                  {progress ? <CourseProgress progress={progress} courseSlug={slug ?? ''} /> : null}
                  <div className="mb-6">
                    <h2 className="font-heading text-h2 font-bold text-brand-navy mb-2">Course Curriculum</h2>
                    <p className="font-body text-body-sm text-ink-muted mb-4">
                      {modules.length} modules
                      {/* Said here, in the curriculum, where somebody is deciding.
                          Two of these courses say in their own intro that "the
                          recordings teach", one of them at $299, and until this
                          the only way to learn how many lessons that meant was to
                          buy the course. A paying customer did exactly that on
                          30 Aug 2026 and wrote in about it. */}
                      {coverage && <span> &middot; {coverageLabel}</span>}
                      {lockedForUser && <span> &middot; free lessons open to everyone</span>}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {included.map((item) => {
                        const meta = lessonKindMeta(item.kind)
                        const Icon = meta?.Icon ?? FaBookOpen
                        return (
                          <span
                            key={item.kind}
                            className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 font-heading text-body-sm ${meta ? meta.chipBg : 'bg-brand-navy/5'}`}
                          >
                            <Icon className={`text-sm ${meta ? meta.chipText : 'text-brand-navy'}`} aria-hidden="true" />
                            <span className="font-bold text-brand-navy">{item.count}</span>
                            <span className="text-ink-soft">{pluralize(item.count, item.noun)}</span>
                          </span>
                        )
                      })}
                    </div>
                    {exerciseSet && (
                      <Link
                        to={`/courses/${slug}/exercises`}
                        className="mt-3 inline-flex items-center gap-2 font-heading text-caption font-bold text-brand-navy underline decoration-brand-navy/40 decoration-1 underline-offset-2 hover:decoration-brand-navy transition-colors duration-150"
                      >
                        <FaGithub className="text-sm" aria-hidden="true" />
                        {exerciseSet.linkLabel ??
                          `Every module has a hands-on exercise. See all ${exerciseSet.exercises.length}`}
                        <FaArrowRight className="text-[10px]" aria-hidden="true" />
                      </Link>
                    )}
                  </div>

                  <div className="space-y-3">
                    {modules.map((mod, modIdx) => {
                      const isOpen = openModules.has(mod.slug)
                      return (
                        <div key={mod.slug} className="rounded-smooth-lg border border-border-subtle bg-white overflow-hidden">
                          <button
                            onClick={() => toggleModule(mod.slug)}
                            aria-expanded={isOpen}
                            aria-controls={`module-lessons-${mod.slug}`}
                            className="flex w-full items-center justify-between px-5 py-4 md:px-6 md:py-5 text-left transition-colors duration-150 hover:bg-brand-cream/50 focus:outline-none"
                          >
                            <div className="flex items-center gap-4 flex-1 min-w-0">
                              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-navy text-white font-heading text-caption font-bold">
                                {modIdx + 1}
                              </span>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-heading text-body font-bold text-brand-navy">{mod.title}</h3>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                              <span className="hidden sm:inline font-heading text-caption text-ink-muted">
                                {mod.lessons.length} lesson{mod.lessons.length !== 1 ? 's' : ''}
                              </span>
                              <span className="text-ink-muted">
                                {isOpen ? <FaChevronDown className="text-xs" /> : <FaChevronRight className="text-xs" />}
                              </span>
                            </div>
                          </button>

                          {/* Every module's lessons stay in the markup and are hidden when
                              collapsed, rather than being conditionally rendered. A closed
                              accordion used to keep those links out of the served HTML, so a
                              crawler only ever saw module 1. */}
                          <div
                            id={`module-lessons-${mod.slug}`}
                            hidden={!isOpen}
                            className="border-t border-border-subtle"
                          >
                            {mod.lessons.map((lesson, lessonIdx) => {
                              const locked = lockedForUser && !lesson.isFree
                              const kindMeta = lessonKindMeta(lesson.kind)
                              return (
                                <Link
                                  key={lesson.slug}
                                  to={`/courses/${slug}/${lesson.slug}`}
                                  className={`flex items-center gap-4 px-5 py-3.5 md:px-6 md:py-4 transition-colors duration-150 border-b border-border-subtle last:border-b-0 group ${kindMeta ? kindMeta.rowBg : 'hover:bg-brand-cream/50'}`}
                                >
                                  {kindMeta ? (
                                    <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${kindMeta.chipBg} ${kindMeta.chipText}`}>
                                      <kindMeta.Icon className="text-xs" aria-hidden="true" />
                                    </span>
                                  ) : (
                                    <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand-cream text-ink-muted font-heading text-overline font-bold group-hover:bg-brand-navy group-hover:text-white transition-colors duration-150">
                                      {lessonIdx + 1}
                                    </span>
                                  )}
                                  <span className="flex-1 min-w-0 font-body text-body-sm text-ink-soft group-hover:text-brand-navy transition-colors duration-150">
                                    {kindMeta && (
                                      <span className={`block font-heading text-overline uppercase tracking-wider ${kindMeta.labelText}`}>{kindMeta.label}</span>
                                    )}
                                    {kindMeta ? lessonDisplayTitle(lesson.title, kindMeta) : lesson.title}
                                  </span>
                                  {/* Only shown where a recording exists. An
                                      absent marker on every row would read as an
                                      accusation rather than as information, and
                                      hasVideo is undefined on an older backend,
                                      where "unknown" must not render as "no". */}
                                  {/* A tick on a lesson already read. Only where
                                      it is true: an empty circle on 119 rows is
                                      a to-do list nobody asked for. */}
                                  {progress?.completed.includes(lesson.slug) && (
                                    <span
                                      className="font-heading text-body-sm font-bold text-brand-navy flex-shrink-0"
                                      aria-label="Completed"
                                      title="You have marked this lesson done"
                                    >
                                      ✓
                                    </span>
                                  )}
                                  {lesson.hasVideo && (
                                    <FaPlayCircle
                                      className="text-xs text-brand-navy/50 flex-shrink-0"
                                      aria-label="Has a recording"
                                      title="This lesson has a recording"
                                    />
                                  )}
                                  {lockedForUser && lesson.isFree && (
                                    <span className="font-heading text-overline uppercase tracking-wider text-brand-sky flex-shrink-0">Free</span>
                                  )}
                                  {locked ? (
                                    <FaLock className="text-xs text-ink-muted flex-shrink-0" aria-label="Locked" />
                                  ) : (
                                    <FaArrowRight className="text-xs text-ink-muted opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex-shrink-0" aria-hidden="true" />
                                  )}
                                </Link>
                              )
                            })}
                            {exerciseSet && exerciseFor(slug, mod.slug) && (
                              <a
                                href={exerciseUrl(exerciseSet.repoUrl, mod.slug)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-4 px-5 py-3.5 md:px-6 md:py-4 bg-brand-cream/40 transition-colors duration-150 hover:bg-brand-cream group"
                              >
                                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-brand-navy text-white">
                                  <FaGithub className="text-xs" aria-hidden="true" />
                                </span>
                                <span className="flex-1 min-w-0">
                                  <span className="block font-heading text-overline uppercase tracking-wider text-brand-navy">Practice exercise</span>
                                  <span className="block font-body text-body-sm text-ink-soft group-hover:text-brand-navy transition-colors duration-150">
                                    {exerciseFor(slug, mod.slug)!.title}
                                  </span>
                                </span>
                                <FaExternalLinkAlt className="text-[10px] text-ink-muted flex-shrink-0" aria-hidden="true" />
                              </a>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>

              {/* Right: What You'll Learn / Buy card */}
              <div className="lg:col-span-2">
                <div className="lg:sticky lg:top-24 border border-border-subtle rounded-smooth-lg bg-white p-6 md:p-8">
                  {lockedForUser ? (
                    <>
                      <div className="mb-5">
                        <span className="font-heading text-display font-bold text-brand-navy">${course.price}</span>
                        <span className="font-body text-body-sm text-ink-muted ml-2">one-time · lifetime access</span>
                        <p className="font-body text-body-sm text-ink-muted mt-1">
                          <span className="line-through">$1,500 live cohort</span> · same curriculum, self-paced
                        </p>
                      </div>
                      <button
                        onClick={() => handleCheckout('course_buy_card')}
                        disabled={checkoutBusy}
                        className="w-full mb-3 inline-flex items-center justify-center gap-2 bg-brand-navy text-white font-heading text-cta font-bold px-6 py-3.5 rounded-smooth hover:opacity-90 transition-opacity disabled:opacity-60"
                      >
                        {checkoutBusy ? 'Starting checkout…' : 'Unlock full course'}
                      </button>
                      <p className="mb-6 inline-flex items-start gap-2 font-body text-body-sm text-ink-muted">
                        <FaShieldAlt className="text-brand-sky mt-0.5 shrink-0 text-xs" aria-hidden="true" />
                        Full refund if the first module doesn&apos;t change how you design.
                      </p>
                    </>
                  ) : null}
                  <h3 className="font-heading text-h4 font-bold text-brand-navy mb-5 uppercase tracking-wide">What You'll Learn</h3>
                  <ul className="space-y-3">
                    {modules.map((mod) => (
                      <li key={mod.slug} className="flex items-start gap-3">
                        <FaCheckCircle className="text-brand-sky mt-1 flex-shrink-0 text-sm" aria-hidden="true" />
                        <span className="font-body text-body-sm text-ink-soft">{mod.title}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </ContentColumn>
        </section>

        {/* ── Alumni testimonials (social proof) ── */}
        {testimonials.length > 0 && (
          <section id="course-reviews" className="border-t border-border-subtle bg-white py-16 md:py-20">
            <ContentColumn anchor="course-reviews" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="mb-10 flex flex-col gap-3 md:mb-12">
                <div className="flex items-center gap-2" aria-label="Rated 5.0 out of 5">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <FaStar key={i} className="text-brand-sky text-sm" aria-hidden="true" />
                    ))}
                  </div>
                  <span className="font-heading text-body-sm font-bold text-brand-navy">5.0</span>
                  <span className="font-body text-body-sm text-ink-muted">
                    · {testimonials.length} alumni review{testimonials.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <h2 className="font-heading text-h2 font-bold text-brand-navy">What Cohort 1 said</h2>
                <p className="font-body text-body text-ink-soft max-w-2xl">
                  The self-paced course is the same curriculum engineers went through in the live cohort. Here&apos;s what they took away.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 lg:gap-8">
                {testimonials.map((t) => (
                  <figure
                    key={t.id}
                    className="flex flex-col rounded-smooth-lg border border-border-subtle bg-brand-cream/40 p-6 md:p-7"
                  >
                    <FaQuoteLeft className="mb-4 text-lg text-brand-navy/20" aria-hidden="true" />
                    <blockquote className="flex-1 font-body text-body-sm leading-relaxed text-ink-soft">
                      {t.body}
                    </blockquote>
                    <figcaption className="mt-6 border-t border-border-subtle pt-4">
                      <div className="font-heading text-body-sm font-bold text-brand-navy">{t.name}</div>
                      {t.title && <div className="font-body text-caption text-ink-muted">{t.title}</div>}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </ContentColumn>
          </section>
        )}

        {/* ── Bottom CTA ── */}
        <section className="bg-brand-navy py-16 md:py-20">
          <ContentColumn anchor="course-cta" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading text-h2 font-bold text-white mb-4">Ready to level up?</h2>
            <p className="font-body text-body text-white/70 mb-8 max-w-xl mx-auto">
              {totalLessons} lessons across {modules.length} modules{coverage ? `, ${coverageLabel}` : ''}.
              {lockedForUser ? ' Start with the free module, then unlock the rest.' : ' Start from the foundations and work your way to mastery.'}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {primaryCta('course_bottom')}
              {freeCta}
            </div>
          </ContentColumn>
        </section>
      </div>
    </>
  )
}

export default CourseSlugPage
